import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MagnifyingGlassIcon, NewspaperIcon, SparklesIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CalendarDays, ChevronDown, Loader2 } from 'lucide-react';
import BlogCard from '../components/blog/BlogCard';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { API_ENDPOINTS, apiRequest } from '../config/api';

const PAGE_SIZE = 9;

const getInitials = (name) => {
  if (!name) return 'DA';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const BlogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 1, current_page: 1, has_next: false });
  const [loadedPage, setLoadedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const activeCategory = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';

  useDocumentMetadata('Blog - Dakhla', 'Admissions guides, university insights, scholarships and student tips from the Dakhla team.');

  const fetchPage = useCallback(async (pageNum, append = false) => {
    try {
      const params = new URLSearchParams({ page: String(pageNum), limit: String(PAGE_SIZE) });
      if (activeCategory) params.set('category', activeCategory);
      if (search) params.set('search', search);
      const data = await apiRequest(`${API_ENDPOINTS.BLOGS}?${params.toString()}`);
      setBlogs((prev) => (append ? [...prev, ...(data.blogs || [])] : (data.blogs || [])));
      setPagination({
        total_count: data.total_count || 0,
        total_pages: data.total_pages || 1,
        current_page: data.current_page || 1,
        has_next: data.has_next || false,
      });
      setLoadedPage(pageNum);
      return data.has_next || false;
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
      setError('Failed to load blog posts. Please try again.');
      return false;
    }
  }, [activeCategory, search]);

  // First page — re-runs whenever filters change (replaces the list)
  useEffect(() => {
    setLoading(true);
    setError(null);
    setBlogs([]);
    fetchPage(1, false).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchPage(loadedPage + 1, true);
    setLoadingMore(false);
  };

  useEffect(() => {
    apiRequest(API_ENDPOINTS.BLOG_CATEGORIES)
      .then(setCategories)
      .catch((err) => console.error('Failed to fetch categories:', err));
  }, []);

  const applyFilters = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    next.delete('page');
    setSearchParams(next);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    applyFilters({ search: searchInput.trim() });
  };

  const featuredPost = blogs.find((b) => b.is_featured);
  const recentPosts = blogs.filter((b) => b.id !== (featuredPost?.id)).slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-100">      {/* Hero */}
      <div className="relative bg-[#1E256F] text-white py-24 overflow-hidden shadow-sm">
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-4">
              Dakhla Blog
            </h1>
            <p className="text-slate-200 text-base sm:text-lg max-w-4xl mx-auto font-normal mb-6 md:whitespace-nowrap">
              Admissions guides, entry test updates, scholarships, and much more.
            </p>

            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto">
              <div className="bg-white rounded-xl p-1.5 shadow-lg">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search articles, guides, topics..."
                    className="w-full pl-12 pr-28 py-2.5 rounded-lg bg-transparent text-slate-700 placeholder-slate-400 text-sm font-medium focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-lg bg-[#1E256F] text-white text-sm font-bold hover:bg-[#161B52] transition-colors cursor-pointer"
                  >
                    Search
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Category filter bar */}
      <div className="sticky top-16 z-20 bg-slate-100/90 backdrop-blur-sm border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => applyFilters({ category: '' })}
            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E256F] focus-visible:ring-offset-2 ${
              !activeCategory
                ? 'bg-[#1E256F] text-white border-[#1E256F] shadow-sm'
                : 'bg-white text-slate-600 border-slate-200/60 hover:border-slate-300 hover:text-slate-800'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => applyFilters({ category: cat.slug })}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E256F] focus-visible:ring-offset-2 ${
                activeCategory === cat.slug
                  ? 'bg-[#1E256F] text-white border-[#1E256F] shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200/60 hover:border-slate-300 hover:text-slate-800'
              }`}
            >
              {cat.name}
            </button>
          ))}
          {activeCategory && (
            <button
              onClick={() => applyFilters({ category: '' })}
              className="ml-auto text-xs font-semibold text-[#1E256F] hover:text-[#161B52] whitespace-nowrap cursor-pointer"
            >
              Clear filter ✕
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {loading ? (
          <div className="py-20 flex justify-center">
            <DakhlaButterflyLoader text="Loading articles..." />
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-sm font-semibold text-slate-500">We couldn't load articles. Check your connection and try again.</p>
            <button onClick={() => fetchPage(1, false)} className="mt-4 btn-secondary text-sm cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">Retry</button>
          </div>
        ) : blogs.length === 0 ? (
          <div className="py-20 text-center bg-white rounded-2xl border border-slate-200 shadow-sm">
            <NewspaperIcon className="h-12 w-12 mx-auto text-slate-300" />
            <p className="mt-4 text-base font-bold text-slate-700">{search || activeCategory ? 'No articles match this filter' : 'No articles published yet'}</p>
            <p className="mt-1.5 text-sm text-slate-400 max-w-sm mx-auto">
              {search || activeCategory
                ? 'Try a different category or clear your filters — new guides are published every week.'
                : 'New guides are being written. Check back soon.'}
            </p>
            {(search || activeCategory) && (
              <button
                onClick={() => setSearchParams({})}
                className="mt-5 btn-secondary text-sm cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Featured (big) + recent (right) — only show on default All view */}
            {(!search && !activeCategory && featuredPost) && (
              <section className="mb-10">
                <div className="lg:grid lg:grid-cols-3 lg:gap-6 items-stretch">
                  <div className="lg:col-span-2">
                    <FeaturedCard post={featuredPost} />
                  </div>
                  <div className="mt-6 lg:mt-0">
                    <div className="flex items-center gap-2 mb-3">
                      <SparklesIcon className="h-4 w-4 text-primary-600" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Latest Articles</span>
                    </div>
                    <div className="space-y-3">
                      {recentPosts.map((post) => (
                        <RecentCard key={post.id} post={post} />
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* All blogs */}
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">All Blog Posts</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">{pagination.total_count} article{pagination.total_count === 1 ? '' : 's'}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>

            {/* Load more — no pagination */}
            {pagination.has_next && (
              <div className="mt-10 flex flex-col items-center gap-3">
                <p className="text-xs font-semibold text-slate-400">
                  Showing {blogs.length} of {pagination.total_count} articles
                </p>
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center px-6 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-bold text-primary-600 hover:border-primary-300 hover:bg-primary-50/50 shadow-sm transition-all disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {loadingMore ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ChevronDown className="h-4 w-4 mr-1.5" />}
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Featured — large card, primary theme (no golden accent)             */
/* ------------------------------------------------------------------ */

const FeaturedCard = ({ post }) => {
  const byline = post.author_name || post.author?.name || 'Dakhla Team';
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-primary-200 transition-all duration-300 overflow-hidden flex flex-col h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
      {/* Full-width image with badges */}
      <div className="relative aspect-[16/10] sm:aspect-[21/10] overflow-hidden bg-slate-100">
        {post.featured_image_url ? (
          <img src={post.featured_image_url} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl font-black text-primary-600">
              {post.title.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-lg bg-[#1E256F] text-white text-[10px] font-bold uppercase tracking-wider shadow-sm">
          <SparklesIcon className="h-3 w-3 mr-1" />
          Featured
        </span>
        {post.category && (
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-white/95 backdrop-blur-sm text-[#1E256F] text-[11px] font-bold border border-slate-200/60 shadow-sm">
            {post.category.name}
          </span>
        )}
      </div>
      <div className="p-6 sm:p-7 flex-1 flex flex-col min-w-0">
        <h3 className="text-xl sm:text-2xl font-extrabold text-slate-800 leading-snug line-clamp-2">
          {post.title}
        </h3>
        <p className="mt-2.5 text-sm sm:text-base text-slate-500 leading-relaxed line-clamp-3">{post.excerpt}</p>
        <div className="mt-auto pt-5 flex items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            {post.author?.profile_picture_url ? (
              <img src={post.author.profile_picture_url} alt={byline} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-primary-100/30 shrink-0" />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-50 border border-primary-100/30 text-primary-600 text-[10px] sm:text-xs font-bold uppercase flex items-center justify-center shrink-0">
                {getInitials(byline)}
              </div>
            )}
            <div className="ml-2 sm:ml-3 min-w-0">
              <p className="text-xs font-semibold text-slate-700 truncate">{byline}</p>
              <p className="text-[11px] text-slate-400 flex items-center">
                <CalendarDays className="h-3 w-3 mr-1" />
                {formatDate(post.published_at)}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center text-sm font-bold text-primary-600 group-hover:text-primary-700 transition-colors shrink-0">
            Read article →
          </span>
        </div>
      </div>
    </Link>
  );
};

/* ------------------------------------------------------------------ */
/* Recent — compact row card for the right-hand column                 */
/* ------------------------------------------------------------------ */

const RecentCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex items-center gap-3.5 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary-200 hover:-translate-y-0.5 transition-all duration-300 p-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
  >
    <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-slate-100">
      {post.featured_image_url ? (
        <img src={post.featured_image_url} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xl font-black text-primary-600">
          {post.title.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
    <div className="min-w-0 flex-1">
      {post.category && (
        <span className="text-[11px] font-bold text-[#1E256F]">{post.category.name}</span>
      )}
      <h4 className="mt-0.5 text-sm font-bold text-slate-800 leading-snug line-clamp-2">
        {post.title}
      </h4>
      <p className="mt-1 text-[11px] font-semibold text-slate-400 flex items-center">
        <ClockIcon className="h-3 w-3 mr-1" />
        {post.reading_time_minutes || 1} min read
      </p>
    </div>
  </Link>
);

export default BlogList;
